import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
import type { PropOptions, PropType } from 'vue';
import type { GuideType } from '@/modules/ConfiguratorJoints/composables/useGuidesAndType';
import {
    JOINT_ANGLE_MAX,
    JOINT_ANGLE_MIN,
    JointShape,
} from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';
import {
    JointCanvasTemplateConfiguration,
    JointGuideAssetConfig,
    JointGuideConfig,
} from '@/modules/ConfiguratorJoints/types/canvasTemplate';
import { useActivePivotGlobalConfiguration } from '@/modules/ConfiguratorJoints/composables/useActivePivotGlobalConfiguration';
import { useInteriorArea } from '@/modules/ConfiguratorJoints/composables/useInteriorArea';
import { useInnerShader } from '@/modules/ConfiguratorJoints/composables/useInnerShader';
import { resolveCornerJointGuideAsset } from '@/modules/ConfiguratorJoints/helpers/CornerJointHelper';
import type {
    ArcVisualConfig,
    GuideImageSlot,
    GuidePlacement,
    JointsConfiguratorTemplateProps,
    LocalShape,
    RegularGuideSlot,
    RenderedGuideType,
    SelectedGuideAsset,
    ShapeGuideImages,
    ShapeImagePair,
    StaticArmLine,
} from '@/modules/ConfiguratorJoints/types/jointsConfiguratorTemplate';
import {
    applyArmStretchShift,
    ARM_STRETCH_DIRECTION,
    ArmSide,
    buildStretchedArmSlices,
    CORNER_CAP_FRACTION,
    END_CAP_FRACTION,
    getEndCapStretchFactors,
    getInnerExtraStretch,
    getNearestEdgeDelta,
    getPerCornerGuideStretchPixels,
    isSelectedNonRegularGuide,
} from '@/modules/ConfiguratorJoints/helpers/JointsStretchHelper';


export default /*#__PURE__*/_defineComponent({
  __name: 'JointsConfiguratorTemplate',
  props: {
    selectedShape: {
        required: true,
    } as PropOptions<JointsConfiguratorTemplateProps['selectedShape']> & { required: true },
    angle: { type: Number, required: true },
    guideTypes: {
        type: Object as PropType<JointsConfiguratorTemplateProps['guideTypes']>,
        required: true,
    },
    canvasTemplateConfigurations: {
        type: Object as PropType<JointsConfiguratorTemplateProps['canvasTemplateConfigurations']>,
        required: true,
    },
    productsSizeFactors: {
        type: Object as PropType<NonNullable<JointsConfiguratorTemplateProps['productsSizeFactors']>>,
        default: () => ({}),
    },
    productsSelectedForJoint: {
        type: Array as PropType<NonNullable<JointsConfiguratorTemplateProps['productsSelectedForJoint']>>,
        default: () => [],
    },
    productAssemblyPositions: {
        type: Object as PropType<NonNullable<JointsConfiguratorTemplateProps['productAssemblyPositions']>>,
        default: () => ({}),
    },
    assemblyPositionVisibility: {
        type: Object as PropType<NonNullable<JointsConfiguratorTemplateProps['assemblyPositionVisibility']>>,
        default: () => ({ left: false, right: false }),
    },
    visibleArm: {
        type: String as PropType<NonNullable<JointsConfiguratorTemplateProps['visibleArm']>>,
        default: 'both',
    },
    currentStep: { type: Number, required: true },
    cornerType: {
        required: true,
    } as PropOptions<JointsConfiguratorTemplateProps['cornerType']> & { required: true },
    longerArm: {
        type: String as PropType<JointsConfiguratorTemplateProps['longerArm']>,
        required: true,
    },
},
  emits: ["update:angle"],
  setup(__props, { emit }: { emit: ({
    (event: 'update:angle', value: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;


const { proxy } = getCurrentInstance()!;


const showLeftArm = computed<boolean>(() => props.visibleArm === 'left' || props.visibleArm === 'both');
const showRightArm = computed<boolean>(() => props.visibleArm === 'right' || props.visibleArm === 'both');

// ============================================================================
// Pre-create one HTMLImageElement per half per shape so vue-konva can pick
// them up synchronously on first render — no watcher, no flash.
// ============================================================================
function createImage(url: string): HTMLImageElement {
    const image = new Image();
    image.src = url;
    return image;
}

const preloadedImages = computed<Record<JointShape, ShapeImagePair>>(() =>
    (Object.keys(props.canvasTemplateConfigurations) as JointShape[]).reduce((accumulator, shape) => {
        const template = props.canvasTemplateConfigurations[shape];
        accumulator[shape] = {
            left: createImage(template.images.left.url),
            right: createImage(template.images.right.url),
            inner: template.images.inner ? createImage(template.images.inner.url) : null,
        };
        return accumulator;
    }, {} as Record<JointShape, ShapeImagePair>),
);

// ============================================================================
// Active template + derived stage / image configs
// ============================================================================
const activeTemplate = computed<JointCanvasTemplateConfiguration | null>(() => {
    if (!props.selectedShape) {
        return null;
    }
    return props.canvasTemplateConfigurations[props.selectedShape] ?? null;
});

const activePivotGlobalConfiguration = useActivePivotGlobalConfiguration(activeTemplate);

function getProductIdByArmSide(armSide: ArmSide): number | undefined {
    const position = armSide === 'left' ? 1 : 2;
    const fallbackIndex = armSide === 'left' ? 0 : 1;

    return (
        props.productsSelectedForJoint.find(
            (productFormId) => props.productAssemblyPositions[productFormId] === position,
        ) ?? props.productsSelectedForJoint[fallbackIndex]
    );
}

const leftProductSizeFactor = computed(() => {
    const leftProductId = getProductIdByArmSide('left');
    return leftProductId !== undefined ? props.productsSizeFactors[leftProductId]?.productSizeFactor ?? 1 : 1;
});

const rightProductSizeFactor = computed(() => {
    const rightProductId = getProductIdByArmSide('right');
    return rightProductId !== undefined ? props.productsSizeFactors[rightProductId]?.productSizeFactor ?? 1 : 1;
});

const MIN_STAGE_PADDING = 16;
const STAGE_PADDING_RATIO = 0.1;

function snapCanvasPixel(value: number): number {
    return Math.round(value);
}

// ---------------------------------------------------------------------------
// World-space bounds for a shape whose coords are LOCAL to an arm group.
// Applies: rotate around the group origin by `rotationDeg`, then translate
// by (anchorX, anchorY).
// ---------------------------------------------------------------------------
// Returns the axis-aligned bounding box of a rectangle after rotating it by
// `rotationDeg` and placing its origin at (anchorX, anchorY).
function getWorldBoundsForLocalShape(shape: LocalShape, anchorX: number, anchorY: number, rotationDeg: number) {
    const rotationRad = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    const corners = [
        { x: shape.x, y: shape.y },
        { x: shape.x + shape.width, y: shape.y },
        { x: shape.x + shape.width, y: shape.y + shape.height },
        { x: shape.x, y: shape.y + shape.height },
    ].map((point) => ({
        x: anchorX + point.x * cos - point.y * sin,
        y: anchorY + point.x * sin + point.y * cos,
    }));

    return {
        minX: Math.min(...corners.map((point) => point.x)),
        maxX: Math.max(...corners.map((point) => point.x)),
        minY: Math.min(...corners.map((point) => point.y)),
        maxY: Math.max(...corners.map((point) => point.y)),
    };
}

// Same as getWorldBoundsForLocalShape but sweeps across a rotation range and
// returns the union — used so the stage size stays stable as the angle changes.
function getSweptWorldBoundsForLocalShape(
    shape: LocalShape,
    anchorX: number,
    anchorY: number,
    minRotationDeg: number,
    maxRotationDeg: number,
) {
    const sampleCount = 12;
    let bounds = getWorldBoundsForLocalShape(shape, anchorX, anchorY, minRotationDeg);
    for (let index = 1; index <= sampleCount; index++) {
        const progress = index / sampleCount;
        const rotation = minRotationDeg + (maxRotationDeg - minRotationDeg) * progress;
        bounds = mergeBounds(bounds, getWorldBoundsForLocalShape(shape, anchorX, anchorY, rotation));
    }
    return bounds;
}

// Returns the bounding box of the angle label at a given angle value.
function getAngleLabelBounds(angleValue: number) {
    if (!activeTemplate.value || !activeArcVisualConfig.value) {
        return null;
    }

    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }

    const { arcAnchorX, arcAnchorY } = pivotConfig;
    const { anchorDeg, sweepSign, labelOffsetX, labelOffsetY } = activeArcVisualConfig.value;
    const midDeg = anchorDeg + (sweepSign * angleValue) / 2;
    const midRad = (midDeg * Math.PI) / 180;
    const { startRadius, endRadius } = getArcEdgeRadii(angleValue);
    const labelDistanceFromPivot = (startRadius + endRadius) / 2 + 16;
    const x = arcAnchorX + labelDistanceFromPivot * Math.cos(midRad) + labelOffsetX;
    const y = arcAnchorY + labelDistanceFromPivot * Math.sin(midRad) + labelOffsetY;
    const text = `${angleValue}°`;
    const fontSize = 14;
    const labelWidth = Math.max(text.length * 8, 24);
    const labelHeight = fontSize + 4;

    return {
        minX: x,
        minY: y,
        maxX: x + labelWidth,
        maxY: y + labelHeight,
    };
}

// Returns the union of angle label bounds across the full angle range so the
// stage size doesn't jump as the slider moves.
function getStableAngleLabelBounds() {
    const sampleCount = 12;
    let bounds = getAngleLabelBounds(JOINT_ANGLE_MIN);

    if (!bounds) {
        return null;
    }

    for (let index = 1; index <= sampleCount; index++) {
        const progress = index / sampleCount;
        const angleValue = JOINT_ANGLE_MIN + (JOINT_ANGLE_MAX - JOINT_ANGLE_MIN) * progress;
        const nextBounds = getAngleLabelBounds(angleValue);
        if (nextBounds) {
            bounds = mergeBounds(bounds, nextBounds);
        }
    }

    return bounds;
}

// Merges two bounding boxes into the smallest box that contains both.
function mergeBounds(
    current: { minX: number; minY: number; maxX: number; maxY: number } | null,
    next: { minX: number; minY: number; maxX: number; maxY: number },
) {
    if (!current) {
        return next;
    }

    return {
        minX: Math.min(current.minX, next.minX),
        minY: Math.min(current.minY, next.minY),
        maxX: Math.max(current.maxX, next.maxX),
        maxY: Math.max(current.maxY, next.maxY),
    };
}

const stableContentBounds = computed(() => {
    if (!activeTemplate.value) {
        return null;
    }

    let bounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
    const rotatingHalf = activeTemplate.value.rotatingHalf;
    const minRotationDeg = JOINT_ANGLE_MIN - JOINT_ANGLE_MAX;
    const maxRotationDeg = 0;

    const leftAnchor = leftArmGroupConfig.value;
    const leftSlices = leftImageSlices.value;
    if (showLeftArm.value && leftSlices?.length) {
        leftSlices.forEach((slice) => {
            bounds = mergeBounds(
                bounds,
                rotatingHalf === 'left'
                    ? getSweptWorldBoundsForLocalShape(
                          slice,
                          leftAnchor.x,
                          leftAnchor.y,
                          minRotationDeg,
                          maxRotationDeg,
                      )
                    : getWorldBoundsForLocalShape(slice, leftAnchor.x, leftAnchor.y, 0),
            );
        });
    }

    const rightAnchor = rightArmGroupConfig.value;
    const rightSlices = rightImageSlices.value;
    if (showRightArm.value && rightSlices?.length) {
        rightSlices.forEach((slice) => {
            bounds = mergeBounds(
                bounds,
                rotatingHalf === 'right'
                    ? getSweptWorldBoundsForLocalShape(
                          slice,
                          rightAnchor.x,
                          rightAnchor.y,
                          minRotationDeg,
                          maxRotationDeg,
                      )
                    : getWorldBoundsForLocalShape(slice, rightAnchor.x, rightAnchor.y, 0),
            );
        });
    }

    if (pivotDotConfig.value) {
        const { x, y } = pivotDotConfig.value;
        bounds = mergeBounds(bounds, {
            minX: x - EXTENSION_RADIUS,
            minY: y - EXTENSION_RADIUS,
            maxX: x + EXTENSION_RADIUS,
            maxY: y + EXTENSION_RADIUS,
        });
    }

    const stableAngleLabelBounds = getStableAngleLabelBounds();
    if (stableAngleLabelBounds) {
        bounds = mergeBounds(bounds, stableAngleLabelBounds);
    }

    return bounds;
});

const stagePadding = computed(() => {
    if (!stableContentBounds.value) {
        return {
            horizontal: MIN_STAGE_PADDING,
            vertical: MIN_STAGE_PADDING,
        };
    }

    const contentWidth = stableContentBounds.value.maxX - stableContentBounds.value.minX;
    const contentHeight = stableContentBounds.value.maxY - stableContentBounds.value.minY;

    return {
        horizontal: Math.max(MIN_STAGE_PADDING, contentWidth * STAGE_PADDING_RATIO),
        vertical: Math.max(MIN_STAGE_PADDING, contentHeight * STAGE_PADDING_RATIO),
    };
});

const stageConfig = computed(() => {
    if (!activeTemplate.value) {
        return null;
    }

    const isExternalCorner = props.selectedShape === 'external';
    const stageX = isExternalCorner ? 50 : 0;
    const stageY = isExternalCorner ? -50 : 0;

    const contentWidth = stableContentBounds.value
        ? stableContentBounds.value.maxX - stableContentBounds.value.minX
        : activeTemplate.value.stageConfig.width;
    const contentHeight = stableContentBounds.value
        ? stableContentBounds.value.maxY - stableContentBounds.value.minY
        : activeTemplate.value.stageConfig.height;

    return {
        width: Math.ceil(contentWidth + stagePadding.value.horizontal * 2),
        height: Math.ceil(contentHeight + stagePadding.value.vertical * 2),
        x: stageX,
        y: stageY,
    };
});

const globalOffsetsPerShape: Record<JointShape, { x: number; y: number }> = {
    external: { x: 0, y: 0 },
    internal: { x: 0, y: 0 },
};

const contentGroupConfig = computed(() => {
    if (!stableContentBounds.value) {
        return { x: 0, y: 0 };
    }

    const { x: globalXOffset, y: globalYOffset } = props.selectedShape
        ? globalOffsetsPerShape[props.selectedShape]
        : { x: 0, y: 0 };

    return {
        x: snapCanvasPixel(stagePadding.value.horizontal - stableContentBounds.value.minX + globalXOffset),
        y: snapCanvasPixel(stagePadding.value.vertical - stableContentBounds.value.minY + globalYOffset),
    };
});

// ---------------------------------------------------------------------------
// Rotation of the active half. Same formula works for both shapes because
// each shape rotates its own half around its own pivot/anchor — the visual
// direction is taken care of by which half is the rotating one.
// Konva rotation: degrees, CW positive on screen (y-down).
// At angle = 90 → 0° (neutral).
// ---------------------------------------------------------------------------
const activeRotationDeg = computed(() => props.angle - JOINT_ANGLE_MAX);

// ---------------------------------------------------------------------------
// Per-shape arc & label placement. The arc lives in the "open" side of the
// corner, which is in a different quadrant for each shape:
//   - external: upper-right (inside of the L bend)
//   - internal: bottom-left (outside of the corner, opposite the pieces)
//
// Each entry defines:
//   anchorDeg  – direction (screen polar, 0°=right, 90°=down, -90°=up) of
//                the fixed side of the arc, drawn from the pivot
//   sweepSign  – +1 sweeps CW from anchor as the slider moves below 90°,
//                -1 sweeps CCW
//   labelOffsetX / labelOffsetY – tweak label position (Konva text is
//                anchored at its top-left, so these nudge it to look centred)
// ---------------------------------------------------------------------------
const arcVisualConfigPerShape: Record<JointShape, ArcVisualConfig> = {
    external: {
        anchorDeg: -90, // straight up from pivot
        sweepSign: 1, // sweep toward the right (upper-right quadrant)
        labelOffsetX: -10,
        labelOffsetY: -8,
    },
    internal: {
        anchorDeg: 90, // straight down from pivot
        sweepSign: 1, // sweep toward the left (bottom-left quadrant)
        labelOffsetX: -14,
        labelOffsetY: -8,
    },
};

// ============================================================================
// GUIDE TYPES AND CORNER TYPES
// ---------------------------------------------------------------------------

// ============================================================================
// ARM GROUP TRANSFORMS
// ---------------------------------------------------------------------------
// Each arm lives inside its own v-group. The group's origin is placed at the
// arm's anchor point (where the arm "hinges" into the joint corner):
//   - left arm: image's bottom-right corner
//   - right arm: image's top-left corner
// When the arm rotates, the group rotates as a whole — every child (slices,
// guides, anything else) follows automatically without per-child rotation
// math. Children inside use coords LOCAL to the anchor (anchor = origin).
// ============================================================================
const leftArmGroupConfig = computed(() => {
    if (!props.selectedShape || !activeTemplate.value) {
        return { x: 0, y: 0, rotation: 0 };
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return { x: 0, y: 0, rotation: 0 };
    }
    const rotating = activeTemplate.value.rotatingHalf === 'left';
    const { pivotX, pivotY, leftCornerX, leftCornerY } = pivotConfig;
    return {
        x: rotating ? leftCornerX : pivotX,
        y: rotating ? leftCornerY : pivotY,
        rotation: rotating ? activeRotationDeg.value : 0,
    };
});

const leftImageGroupConfig = computed(() => ({
    x: activeTemplate.value ? activeTemplate.value.imageLeft.x - leftArmGroupConfig.value.x : 0,
    y: activeTemplate.value ? activeTemplate.value.imageLeft.y - leftArmGroupConfig.value.y : 0,
}));

const rightArmGroupConfig = computed(() => {
    if (!props.selectedShape || !activeTemplate.value) {
        return { x: 0, y: 0, rotation: 0, offsetX: 0, offsetY: 0 };
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return { x: 0, y: 0, rotation: 0, offsetX: 0, offsetY: 0 };
    }
    const rotating = activeTemplate.value.rotatingHalf === 'right';
    const { rightCornerX, rightCornerY, rightArmLocalOriginX, rightArmLocalOriginY } = pivotConfig;
    return {
        x: rightCornerX + (rotating ? rightArmLocalOriginX : 0),
        y: rightCornerY + (rotating ? rightArmLocalOriginY : 0),
        rotation: rotating ? activeRotationDeg.value : 0,
        offsetX: rotating ? rightArmLocalOriginX : 0,
        offsetY: rotating ? rightArmLocalOriginY : 0,
    };
});

// ============================================================================
// GUIDE IMAGE CONFIGS
// ---------------------------------------------------------------------------
// Each arm has two guides (top rail + bottom rail of the product). Guides
// live inside their arm's group, so they inherit the arm's translation and
// rotation automatically — no per-guide rotation math needed.
// `selfRotation` (e.g. 90 for guides on horizontal arms) composes on top of
// the group rotation and is applied around the guide's own centre.
// ============================================================================
function buildGuideKonvaConfig(
    guide: JointGuideConfig | JointGuideAssetConfig,
    scale: number,
    guideX: number, // local x relative to arm anchor
    guideY: number, // local y relative to arm anchor
    rotation: number, // initial rotation from activePivotGlobalConfiguration
    image: HTMLImageElement,
    scaleMultiplier = 1,
) {
    const width = guide.widthMm * scale * scaleMultiplier;
    const height = guide.heightMm * scale * scaleMultiplier;
    return {
        image,
        x: guideX,
        y: guideY,
        width,
        height,
        rotation,
    };
}

const guideSlotFallbackArmSide: Record<RegularGuideSlot, ArmSide> = {
    leftTop: 'left',
    leftBottom: 'left',
    rightTop: 'right',
    rightBottom: 'right',
};

function getArmProductSizeFactor(armSide: ArmSide): number {
    return armSide === 'left' ? leftProductSizeFactor.value : rightProductSizeFactor.value;
}

const preloadedGuideImages = computed<Record<JointShape, ShapeGuideImages>>(() =>
    (Object.keys(props.canvasTemplateConfigurations) as JointShape[]).reduce((accumulator, shape) => {
        const g = props.canvasTemplateConfigurations[shape].guides;
        accumulator[shape] = {
            leftTop: g?.leftTop ? createImage(g.leftTop.url) : null,
            leftBottom: g?.leftBottom ? createImage(g.leftBottom.url) : null,
            rightTop: g?.rightTop ? createImage(g.rightTop.url) : null,
            rightBottom: g?.rightBottom ? createImage(g.rightBottom.url) : null,
            wireGuide: g?.wireGuide ? createImage(g.wireGuide.url) : null,
            inoxGuide: g?.inoxGuide ? createImage(g.inoxGuide.url) : null,
            concealedGuideTop: g?.concealedGuideTop ? createImage(g.concealedGuideTop.url) : null,
            concealedGuideBottom: g?.concealedGuideBottom ? createImage(g.concealedGuideBottom.url) : null,
        };
        return accumulator;
    }, {} as Record<JointShape, ShapeGuideImages>),
);

function getGuideImage(shape: JointShape, slot: GuideImageSlot): HTMLImageElement | null {
    return preloadedGuideImages.value[shape]?.[slot] ?? null;
}

function getSelectedGuideAsset(
    regularSlot: RegularGuideSlot,
    selectedGuide: GuideType | null,
): SelectedGuideAsset | null {
    if (!props.selectedShape || !activeTemplate.value) {
        return null;
    }

    const resolvedGuide = resolveCornerJointGuideAsset(activeTemplate.value, regularSlot, selectedGuide);
    if (!resolvedGuide) {
        return null;
    }

    const image = getGuideImage(props.selectedShape, resolvedGuide.imageSlot);

    if (!image) {
        return null;
    }

    return {
        guide: resolvedGuide.asset,
        image,
        renderedGuideType: resolvedGuide.renderedGuideType,
        scale: resolvedGuide.asset.scale ?? 1,
    };
}

function buildSelectedGuideConfig(
    regularSlot: RegularGuideSlot,
    placementsByType: Record<RenderedGuideType, GuidePlacement>,
    selectedGuideType: GuideType | null,
) {
    const selectedGuide = getSelectedGuideAsset(regularSlot, selectedGuideType);
    if (!selectedGuide || !activeTemplate.value) {
        return null;
    }

    const armSide = guideSlotFallbackArmSide[regularSlot];
    const placement = placementsByType[selectedGuide.renderedGuideType];

    // A guide sits ON the arm, so it must move exactly like the arm's stretched
    // image (leftImageSlices / rightImageSlices): points on the pinned corner
    // cap don't move, points on the straight middle move proportionally, and
    // points on the rigid far end-cap move by the full delta. The stretch
    // factor comes from the SAME source as the arm image — getArmProductSizeFactor
    // → leftProductSizeFactor / rightProductSizeFactor → getProductIdByArmSide,
    // which resolves the product for this arm via productAssemblyPositions.
    const { scale, images } = activeTemplate.value;
    const armImage = images[armSide];
    const vertical = armImage.heightMm > armImage.widthMm;
    const dir = ARM_STRETCH_DIRECTION[armSide];
    const renderedLength = (vertical ? armImage.heightMm : armImage.widthMm) * scale;
    const stretchFactor = getArmProductSizeFactor(armSide);
    const pinnedCornerLength = renderedLength * CORNER_CAP_FRACTION;
    const stretchableMiddleLength = renderedLength * (1 - CORNER_CAP_FRACTION - END_CAP_FRACTION);
    const totalStretchAmount = (stretchFactor - 1) * renderedLength;
    const guidePositionOnAxis = vertical ? placement.y : placement.x;
    // Convert the signed axis position to an unsigned distance from the corner.
    // Multiplying by dir flips the sign for the left arm (dir=-1, positions are
    // negative) so that all further comparisons are > 0 regardless of arm side.
    const distanceAlongArm = guidePositionOnAxis * dir;
    let guideShift = 0;
    if (distanceAlongArm > pinnedCornerLength + stretchableMiddleLength) {
        // Guide is on the rigid far end-cap — it moves by the full stretch amount.
        guideShift = totalStretchAmount;
    } else if (distanceAlongArm > pinnedCornerLength) {
        // Guide is in the stretching middle section — it moves proportionally to
        // how far it sits inside that section (0 at the corner boundary, full at the end).
        guideShift = ((distanceAlongArm - pinnedCornerLength) / stretchableMiddleLength) * totalStretchAmount;
    }
    // Apply the normal outer-arm/product-size stretch first. The type-1 extra
    // inner stretch is added below from the actual transformed inner image bounds.
    const shiftedAxisPosition = guidePositionOnAxis + guideShift * dir;
    const shifted = {
        x: vertical ? placement.x : shiftedAxisPosition,
        y: vertical ? shiftedAxisPosition : placement.y,
    };
    const innerStretchShift = getInnerGuideStretchShift(armSide, shifted);

    return buildGuideKonvaConfig(
        selectedGuide.guide,
        activeTemplate.value.scale,
        shifted.x + innerStretchShift.x,
        shifted.y + innerStretchShift.y,
        placement.rotation,
        selectedGuide.image,
        selectedGuide.scale,
    );
}

const leftTopGuideConfig = computed(() => {
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }

    return buildSelectedGuideConfig(
        'leftTop',
        {
            regular: {
                x: pivotConfig.regularGuideOneLeftArmX,
                y: pivotConfig.regularGuideOneLeftArmY,
                rotation: pivotConfig.regularGuideOneLeftArmRotation,
            },
            wireGuide: {
                x: pivotConfig.wireGuideOneLeftArmX,
                y: pivotConfig.wireGuideOneLeftArmY,
                rotation: pivotConfig.wireGuideOneLeftArmRotation,
            },
            inoxGuide: {
                x: pivotConfig.inoxGuideOneLeftArmX,
                y: pivotConfig.inoxGuideOneLeftArmY,
                rotation: pivotConfig.inoxGuideOneLeftArmRotation,
            },
            concealedGuide: {
                x: pivotConfig?.concealedGuideOneLeftArmX ?? pivotConfig.regularGuideOneLeftArmX,
                y: pivotConfig?.concealedGuideOneLeftArmY ?? pivotConfig.regularGuideOneLeftArmY,
                rotation: pivotConfig?.concealedGuideOneLeftArmRotation ?? pivotConfig.regularGuideOneLeftArmRotation,
            },
        },
        props.guideTypes[1].rightCorner!,
    );
});

const leftBottomGuideConfig = computed(() => {
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    return buildSelectedGuideConfig(
        'leftBottom',
        {
            regular: {
                x: pivotConfig.regularGuideTwoLeftArmX,
                y: pivotConfig.regularGuideTwoLeftArmY,
                rotation: pivotConfig.regularGuideTwoLeftArmRotation,
            },
            wireGuide: {
                x: pivotConfig.wireGuideTwoLeftArmX,
                y: pivotConfig.wireGuideTwoLeftArmY,
                rotation: pivotConfig.wireGuideTwoLeftArmRotation,
            },
            inoxGuide: {
                x: pivotConfig.inoxGuideTwoLeftArmX,
                y: pivotConfig.inoxGuideTwoLeftArmY,
                rotation: pivotConfig.inoxGuideTwoLeftArmRotation,
            },
            concealedGuide: {
                x: pivotConfig?.concealedGuideTwoLeftArmX ?? pivotConfig.regularGuideTwoLeftArmX,
                y: pivotConfig?.concealedGuideTwoLeftArmY ?? pivotConfig.regularGuideTwoLeftArmY,
                rotation: pivotConfig?.concealedGuideTwoLeftArmRotation ?? pivotConfig.regularGuideTwoLeftArmRotation,
            },
        },
        props.guideTypes[1].leftCorner!,
    );
});

const rightTopGuideConfig = computed(() => {
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    return buildSelectedGuideConfig(
        'rightTop',
        {
            regular: {
                x: pivotConfig.regularGuideOneRightArmX,
                y: pivotConfig.regularGuideOneRightArmY,
                rotation: pivotConfig.regularGuideOneRightArmRotation,
            },
            wireGuide: {
                x: pivotConfig.wireGuideOneRightArmX,
                y: pivotConfig.wireGuideOneRightArmY,
                rotation: pivotConfig.wireGuideOneRightArmRotation,
            },
            inoxGuide: {
                x: pivotConfig.inoxGuideOneRightArmX,
                y: pivotConfig.inoxGuideOneRightArmY,
                rotation: pivotConfig.inoxGuideOneRightArmRotation,
            },
            concealedGuide: {
                x: pivotConfig?.concealedGuideOneRightArmX ?? pivotConfig.regularGuideOneRightArmX,
                y: pivotConfig?.concealedGuideOneRightArmY ?? pivotConfig.regularGuideOneRightArmY,
                rotation: pivotConfig?.concealedGuideOneRightArmRotation ?? pivotConfig.regularGuideOneRightArmRotation,
            },
        },
        props.guideTypes[2].rightCorner!,
    );
});

const rightBottomGuideConfig = computed(() => {
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    return buildSelectedGuideConfig(
        'rightBottom',
        {
            regular: {
                x: pivotConfig.regularGuideTwoRightArmX,
                y: pivotConfig.regularGuideTwoRightArmY,
                rotation: pivotConfig.regularGuideTwoRightArmRotation,
            },
            wireGuide: {
                x: pivotConfig.wireGuideTwoRightArmX,
                y: pivotConfig.wireGuideTwoRightArmY,
                rotation: pivotConfig.wireGuideTwoRightArmRotation,
            },
            inoxGuide: {
                x: pivotConfig.inoxGuideTwoRightArmX,
                y: pivotConfig.inoxGuideTwoRightArmY,
                rotation: pivotConfig.inoxGuideTwoRightArmRotation,
            },
            concealedGuide: {
                x: pivotConfig?.concealedGuideTwoRightArmX ?? pivotConfig.regularGuideTwoRightArmX,
                y: pivotConfig?.concealedGuideTwoRightArmY ?? pivotConfig.regularGuideTwoRightArmY,
                rotation: pivotConfig?.concealedGuideTwoRightArmRotation ?? pivotConfig.regularGuideTwoRightArmRotation,
            },
        },
        props.guideTypes[2].leftCorner!,
    );
});

// ============================================================================
// Angle indicator — arc swept between the two outer arm edges. The configured
// anchor controls its centre, while the radii are measured from that anchor to
// the actual dotted arm boundaries so moving the anchor cannot shorten the arc.
// ============================================================================
// Fallback radius for shapes that do not derive their reach from arm edges.
const ARC_RADIUS = 56;
const ARC_SAMPLES = 48;
// Stable bounds reserved around the anchor for the complete indicator.
const EXTENSION_RADIUS = 110;

const activeArcVisualConfig = computed<ArcVisualConfig | null>(() => {
    if (!props.selectedShape) {
        return null;
    }
    return arcVisualConfigPerShape[props.selectedShape];
});

function transformArmLocalPointToWorld(
    point: { x: number; y: number },
    group: { x: number; y: number; rotation: number; offsetX?: number; offsetY?: number },
) {
    const rotationRad = (group.rotation * Math.PI) / 180;
    const localX = point.x - (group.offsetX ?? 0);
    const localY = point.y - (group.offsetY ?? 0);

    return {
        x: group.x + localX * Math.cos(rotationRad) - localY * Math.sin(rotationRad),
        y: group.y + localX * Math.sin(rotationRad) + localY * Math.cos(rotationRad),
    };
}

function getRayToLineDistance(
    origin: { x: number; y: number },
    rayDeg: number,
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number },
): number | null {
    const rayRad = (rayDeg * Math.PI) / 180;
    const rayDirection = { x: Math.cos(rayRad), y: Math.sin(rayRad) };
    const lineDirection = { x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y };
    const denominator = rayDirection.x * lineDirection.y - rayDirection.y * lineDirection.x;

    if (Math.abs(denominator) < 0.0001) {
        return null;
    }

    const distance =
        ((lineStart.x - origin.x) * lineDirection.y - (lineStart.y - origin.y) * lineDirection.x) / denominator;

    return distance > 0 ? distance : null;
}

function getArcEdgeRadii(angleValue: number): { startRadius: number; endRadius: number } {
    if (!props.selectedShape) {
        return { startRadius: ARC_RADIUS, endRadius: ARC_RADIUS };
    }

    const pivotConfig = activePivotGlobalConfiguration.value;
    const leftOuterEdge = leftArmStaticLineConfigs.value[1]?.points;
    const rightOuterEdge = rightArmStaticLineConfigs.value[1]?.points;

    if (!leftOuterEdge || !rightOuterEdge) {
        return { startRadius: ARC_RADIUS, endRadius: ARC_RADIUS };
    }

    const origin = { x: pivotConfig.arcAnchorX, y: pivotConfig.arcAnchorY };
    const isExternal = props.selectedShape === 'external';
    const startEdge = isExternal ? leftOuterEdge : rightOuterEdge;
    const endEdge = isExternal ? rightOuterEdge : leftOuterEdge;
    const startGroup = isExternal
        ? {
              x: pivotConfig.pivotX,
              y: pivotConfig.pivotY,
              rotation: 0,
          }
        : {
              x: pivotConfig.rightCornerX,
              y: pivotConfig.rightCornerY,
              rotation: 0,
          };
    const endGroup = isExternal
        ? {
              x: pivotConfig.rightCornerX + pivotConfig.rightArmLocalOriginX,
              y: pivotConfig.rightCornerY + pivotConfig.rightArmLocalOriginY,
              rotation: angleValue - JOINT_ANGLE_MAX,
              offsetX: pivotConfig.rightArmLocalOriginX,
              offsetY: pivotConfig.rightArmLocalOriginY,
          }
        : {
              x: pivotConfig.leftCornerX,
              y: pivotConfig.leftCornerY,
              rotation: angleValue - JOINT_ANGLE_MAX,
          };
    const startLineStart = transformArmLocalPointToWorld({ x: startEdge[0], y: startEdge[1] }, startGroup);
    const startLineEnd = transformArmLocalPointToWorld({ x: startEdge[2], y: startEdge[3] }, startGroup);
    const endLineStart = transformArmLocalPointToWorld({ x: endEdge[0], y: endEdge[1] }, endGroup);
    const endLineEnd = transformArmLocalPointToWorld({ x: endEdge[2], y: endEdge[3] }, endGroup);
    const { anchorDeg, sweepSign } = arcVisualConfigPerShape[props.selectedShape];

    return {
        startRadius: getRayToLineDistance(origin, anchorDeg, startLineStart, startLineEnd) ?? ARC_RADIUS,
        endRadius:
            getRayToLineDistance(origin, anchorDeg + sweepSign * angleValue, endLineStart, endLineEnd) ?? ARC_RADIUS,
    };
}

const isAngleDragStep = computed(() => props.currentStep === 3);

const activeDraggableArm = computed<'left' | 'right' | null>(() => {
    if (!activeTemplate.value || !isAngleDragStep.value) {
        return null;
    }
    return activeTemplate.value.rotatingHalf;
});

let angleDragState: {
    startClientY: number;
    startAngle: number;
} | null = null;

const ANGLE_DRAG_PIXELS_PER_DEGREE = 4;
const dragDirectionPerShape: Record<JointShape, 1 | -1> = {
    external: -1,
    internal: 1,
};

function clampAngle(angleValue: number): number {
    return Math.min(JOINT_ANGLE_MAX, Math.max(JOINT_ANGLE_MIN, Math.round(angleValue)));
}

function getClientPointFromEvent(event: any): { x: number; y: number } | null {
    const sourceEvent = event?.evt ?? event;
    const touch = sourceEvent?.touches?.[0] ?? sourceEvent?.changedTouches?.[0];
    if (touch) {
        return { x: touch.clientX, y: touch.clientY };
    }
    if (typeof sourceEvent?.clientX === 'number' && typeof sourceEvent?.clientY === 'number') {
        return { x: sourceEvent.clientX, y: sourceEvent.clientY };
    }
    return null;
}

function updateAngleFromPointerEvent(event: any) {
    if (!angleDragState) {
        return;
    }
    event?.preventDefault?.();
    const clientPoint = getClientPointFromEvent(event);
    if (!clientPoint) {
        return;
    }

    const verticalPixelsMoved = angleDragState.startClientY - clientPoint.y;
    const dragDirection = props.selectedShape ? dragDirectionPerShape[props.selectedShape] : 1;
    const nextAngle = clampAngle(
        angleDragState.startAngle + (verticalPixelsMoved * dragDirection) / ANGLE_DRAG_PIXELS_PER_DEGREE,
    );
    if (nextAngle !== props.angle) {
        emit('update:angle', nextAngle);
    }
}

function addAngleDragListeners() {
    window.addEventListener('mousemove', updateAngleFromPointerEvent);
    window.addEventListener('mouseup', stopAngleDrag);
    window.addEventListener('touchmove', updateAngleFromPointerEvent, { passive: false });
    window.addEventListener('touchend', stopAngleDrag);
    window.addEventListener('touchcancel', stopAngleDrag);
}

function removeAngleDragListeners() {
    window.removeEventListener('mousemove', updateAngleFromPointerEvent);
    window.removeEventListener('mouseup', stopAngleDrag);
    window.removeEventListener('touchmove', updateAngleFromPointerEvent);
    window.removeEventListener('touchend', stopAngleDrag);
    window.removeEventListener('touchcancel', stopAngleDrag);
}

function setStageCursor(cursor: string) {
    const container = (document.querySelector('.joints-configurator-template canvas') as HTMLCanvasElement | null)
        ?.parentElement;
    if (container) {
        container.style.cursor = cursor;
    }
}

function setArmCursor(arm: 'left' | 'right') {
    if (activeDraggableArm.value !== arm) {
        return;
    }
    setStageCursor('grab');
}

function resetArmCursor() {
    setStageCursor('default');
}

function startAngleDrag(arm: 'left' | 'right', event: any) {
    if (activeDraggableArm.value !== arm || !showAngleIndicator.value) {
        return;
    }
    const clientPoint = getClientPointFromEvent(event);
    if (!clientPoint) {
        return;
    }

    event?.evt?.preventDefault?.();
    event.cancelBubble = true;
    angleDragState = {
        startClientY: clientPoint.y,
        startAngle: props.angle,
    };
    addAngleDragListeners();
}

function stopAngleDrag() {
    angleDragState = null;
    removeAngleDragListeners();
    resetArmCursor();
}

onBeforeUnmount(stopAngleDrag);

const arcConfig = computed(() => {
    if (!activeTemplate.value || !activeArcVisualConfig.value) {
        return null;
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    const { arcAnchorX, arcAnchorY } = pivotConfig;
    const { anchorDeg, sweepSign } = activeArcVisualConfig.value;
    // Arc span equals the slider value: at angle=45 the arc covers 45°, at
    // angle=90 it covers 90°, at angle=135 it covers 135°. sweepSign picks
    // which side of the anchor the arc opens to.
    const startDeg = anchorDeg;
    const endDeg = anchorDeg + sweepSign * props.angle;
    const { startRadius, endRadius } = getArcEdgeRadii(props.angle);
    const points: number[] = [];
    for (let i = 0; i <= ARC_SAMPLES; i++) {
        const progressAlongArc = i / ARC_SAMPLES;
        const currentAngleDeg = startDeg + (endDeg - startDeg) * progressAlongArc;
        const currentAngleRad = (currentAngleDeg * Math.PI) / 180;
        const currentRadius = startRadius + (endRadius - startRadius) * progressAlongArc;
        points.push(
            arcAnchorX + currentRadius * Math.cos(currentAngleRad),
            arcAnchorY + currentRadius * Math.sin(currentAngleRad),
        );
    }
    return {
        points,
        stroke: '#1976d2',
        strokeWidth: 2,
        lineCap: 'round',
        lineJoin: 'round',
        dash: [6, 4],
    };
});

// Per-shape extra distance along each arm's radial, added to its measured edge
// radius when building the dashed grey triangle. Bumping this scales the
// WHOLE triangle uniformly outward — start points slide further down their
// arms and the apex slides further down the bisector. The ARK
// (arc/pivot/label) is untouched.
const extensionTriangleRadialOffsetPerShape: Record<JointShape, number> = {
    external: 0,
    internal: 0,
};

const edgeLineRadialOffsetPerShape: Record<JointShape, number> = {
    external: 0,
    internal: -17,
};

const secondEdgeLineXOffsetPerShape: Record<JointShape, number> = {
    external: 4,
    internal: -1,
};

// Three dashed grey lines that visualise the angle's apex past the joint:
//   - two "side" lines starting at each measured arm edge (plus any per-shape
//     offset), running TANGENT to the arc until they meet at the
//     apex on the bisector
//   - the bisector from the pivot to that same apex
// Apex sits at baseRadius / cos(θ/2) along the bisector. This matches the
// "perpendicular to opposite arm" look at θ=90° and naturally shortens the
// side lines as θ moves away from 90°, instead of stretching them outward.
const extensionLineConfigs = computed(() => {
    if (!activeTemplate.value || !activeArcVisualConfig.value || !props.selectedShape) {
        return [];
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return [];
    }
    const { arcAnchorX, arcAnchorY } = pivotConfig;
    const { anchorDeg, sweepSign } = activeArcVisualConfig.value;
    const { startRadius, endRadius } = getArcEdgeRadii(props.angle);
    const radialOffset = extensionTriangleRadialOffsetPerShape[props.selectedShape];
    const startBaseRadius = startRadius + radialOffset;
    const endBaseRadius = endRadius + radialOffset;
    const averageBaseRadius = (startBaseRadius + endBaseRadius) / 2;
    const startDeg = anchorDeg;
    const endDeg = anchorDeg + sweepSign * props.angle;
    const midDeg = anchorDeg + (sweepSign * props.angle) / 2;

    const pointOnRay = (deg: number, radius: number) => {
        const rad = (deg * Math.PI) / 180;
        return {
            x: arcAnchorX + radius * Math.cos(rad),
            y: arcAnchorY + radius * Math.sin(rad),
        };
    };

    const apexDistance = averageBaseRadius / Math.cos((props.angle * Math.PI) / 360);
    const apex = pointOnRay(midDeg, apexDistance);
    const sideStartFrom = pointOnRay(startDeg, startBaseRadius);
    const sideEndFrom = pointOnRay(endDeg, endBaseRadius);

    const sharedStyle = {
        stroke: '#9e9e9e',
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        dash: [3, 3],
    } as const;

    return [
        { ...sharedStyle, points: [arcAnchorX, arcAnchorY, apex.x, apex.y] },
        { ...sharedStyle, points: [sideStartFrom.x, sideStartFrom.y, apex.x, apex.y] },
        { ...sharedStyle, points: [sideEndFrom.x, sideEndFrom.y, apex.x, apex.y] },
    ];
});

const edgeLineConfigs = computed(() => {
    if (!activeTemplate.value || !activeArcVisualConfig.value || !props.selectedShape) {
        return [];
    }

    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return [];
    }

    const { arcAnchorXTypeOne, arcAnchorYTypeOne } = pivotConfig;
    const { anchorDeg, sweepSign } = activeArcVisualConfig.value;
    const baseRadius = ARC_RADIUS + edgeLineRadialOffsetPerShape[props.selectedShape];
    const startDeg = anchorDeg;
    const endDeg = anchorDeg + sweepSign * props.angle;
    const midDeg = anchorDeg + (sweepSign * props.angle) / 2;

    const pointOnRay = (deg: number, radius: number) => {
        const rad = (deg * Math.PI) / 180;
        return {
            x: arcAnchorXTypeOne + radius * Math.cos(rad),
            y: arcAnchorYTypeOne + radius * Math.sin(rad),
        };
    };

    const apexDistance = baseRadius / Math.cos((props.angle * Math.PI) / 360);
    const apex = pointOnRay(midDeg, apexDistance);
    const sideStartFrom = pointOnRay(startDeg, baseRadius);
    const sideEndFrom = pointOnRay(endDeg, baseRadius);
    const secondLineXOffset = secondEdgeLineXOffsetPerShape[props.selectedShape];

    const sharedStyle = {
        stroke: '#9e9e9e',
        strokeWidth: 1.5,
        lineCap: 'round',
        lineJoin: 'round',
        dash: [3, 3],
    } as const;

    return [
        { ...sharedStyle, points: [sideStartFrom.x, sideStartFrom.y, apex.x, apex.y] },
        {
            ...sharedStyle,
            points: [sideEndFrom.x + secondLineXOffset, sideEndFrom.y, apex.x + secondLineXOffset, apex.y],
        },
    ];
});

const pivotDotConfig = computed(() => {
    if (!activeTemplate.value) {
        return null;
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    const { arcAnchorX, arcAnchorY } = pivotConfig;
    return {
        x: arcAnchorX,
        y: arcAnchorY,
        radius: 4,
        fill: '#d32f2f',
    };
});

const angleLabelConfig = computed(() => {
    if (!activeTemplate.value || !activeArcVisualConfig.value) {
        return null;
    }
    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }
    const { arcAnchorX, arcAnchorY } = pivotConfig;
    const { anchorDeg, sweepSign, labelOffsetX, labelOffsetY } = activeArcVisualConfig.value;
    // Place label on the bisector of the arc, just outside the radius.
    const midDeg = anchorDeg + (sweepSign * props.angle) / 2;
    const midRad = (midDeg * Math.PI) / 180;
    const { startRadius, endRadius } = getArcEdgeRadii(props.angle);
    const labelDistanceFromPivot = (startRadius + endRadius) / 2 + 16;
    return {
        x: arcAnchorX + labelDistanceFromPivot * Math.cos(midRad) + labelOffsetX,
        y: arcAnchorY + labelDistanceFromPivot * Math.sin(midRad) + labelOffsetY,
        text: `${props.angle}°`,
        fontSize: 14,
        fontStyle: 'bold',
        fill: '#1976d2',
    };
});

const showAngleIndicator = computed(() => {
    return props.selectedShape !== null && props.currentStep >= 3;
});

// ---------------------------------------------------------------------------
// Left arm slices — always 3-sliced. Only the straight middle section
// stretches by leftFactor; the corner cap and end cap stay original size.
// Positions are LOCAL to the left arm group's anchor (= image's bottom-right
// corner). Image extends up-and-left into negative space.
// ---------------------------------------------------------------------------
const leftImageSlices = computed(() => {
    if (!props.selectedShape || !activeTemplate.value) return null;
    return buildStretchedArmSlices({
        imageAsset: activeTemplate.value.images.left,
        image: preloadedImages.value[props.selectedShape].left,
        scale: activeTemplate.value.scale,
        factor: leftProductSizeFactor.value,
        arm: 'left',
    });
});

// Keep the shader's image-derived bounds aligned with the image-only group.
// Its end cutoffs still come from the live dotted-border coordinates.
const positionedLeftImageSlices = computed(() =>
    leftImageSlices.value?.map((slice) => ({
        ...slice,
        x: slice.x + leftImageGroupConfig.value.x,
        y: slice.y + leftImageGroupConfig.value.y,
    })) ?? null,
);

// ---------------------------------------------------------------------------
// Right arm slices — always 3-sliced. Mirror of left arm but grows rightward.
// Positions are LOCAL to the right arm group's anchor (= image's top-left).
// Image extends down-and-right into positive space.
// ---------------------------------------------------------------------------
const rightImageSlices = computed(() => {
    if (!props.selectedShape || !activeTemplate.value) return null;
    return buildStretchedArmSlices({
        imageAsset: activeTemplate.value.images.right,
        image: preloadedImages.value[props.selectedShape].right,
        scale: activeTemplate.value.scale,
        factor: rightProductSizeFactor.value,
        arm: 'right',
    });
});

// Keep Type 1's extra extension based on the full anchor-to-image span even
// when whitespace is trimmed from an SVG and the image itself is repositioned.
function getTypeOneStretchFactor(armSide: ArmSide, factor: number): number {
    if (!activeTemplate.value || factor === 0) return factor;

    const imageAsset = activeTemplate.value.images[armSide];
    const isVertical = imageAsset.heightMm > imageAsset.widthMm;
    const renderedLength = (isVertical ? imageAsset.heightMm : imageAsset.widthMm) * activeTemplate.value.scale;
    const imageGroup = armSide === 'left' ? leftImageGroupConfig.value : { x: 0, y: 0 };
    const imageStart = isVertical ? imageGroup.y : imageGroup.x;
    const imageEnd = imageStart + ARM_STRETCH_DIRECTION[armSide] * renderedLength;
    const fullArmLength = Math.max(Math.abs(imageStart), Math.abs(imageEnd));

    return factor * (fullArmLength / renderedLength);
}

// ---------------------------------------------------------------------------
// Inner overlay slices — the shared inner image uses the arm's product-size
// factor as its base, then applies its own independent per-corner guide stretch.
// The main leftImageSlices / rightImageSlices above are not affected.
// ---------------------------------------------------------------------------
const endCapStretchFactorsPerShape = computed(() => getEndCapStretchFactors(props.selectedShape));

// Type-1 longer-arm factors and their direction are resolved by the shared
// stretch helper. Inner slices and guides both consume that same result.
function getBoundsForLocalShapes(shapes: LocalShape[], anchorX: number, anchorY: number, rotationDeg: number) {
    return shapes.reduce(
        (bounds, shape) => mergeBounds(bounds, getWorldBoundsForLocalShape(shape, anchorX, anchorY, rotationDeg)),
        null as { minX: number; minY: number; maxX: number; maxY: number } | null,
    );
}

function getInnerGuideStretchShift(
    armSide: ArmSide,
    guidePosition: { x: number; y: number },
): { x: number; y: number } {
    const extra = getInnerExtraStretch({
        armSide,
        cornerType: props.cornerType,
        longerArm: props.longerArm,
        stretchFactors: endCapStretchFactorsPerShape.value,
    });
    if (!extra.factor || !props.selectedShape || !activeTemplate.value?.images.inner) {
        return { x: 0, y: 0 };
    }

    const innerImage = preloadedImages.value[props.selectedShape].inner;
    if (!innerImage) {
        return { x: 0, y: 0 };
    }

    const innerGroup = armSide === 'left' ? leftInnerGroupConfig.value : rightInnerGroupConfig.value;
    const imageAsset = activeTemplate.value.images.inner;
    const extraFactor = getTypeOneStretchFactor(armSide, extra.factor);
    const baseFactor = getArmProductSizeFactor(armSide);
    const baseSlices = buildStretchedArmSlices({
        imageAsset,
        stretchReferenceAsset: activeTemplate.value.images[armSide],
        image: innerImage,
        scale: activeTemplate.value.scale,
        factor: baseFactor,
        arm: armSide,
    });
    const stretchedSlices = buildStretchedArmSlices({
        imageAsset,
        stretchReferenceAsset: activeTemplate.value.images[armSide],
        image: innerImage,
        scale: activeTemplate.value.scale,
        factor: baseFactor + extraFactor,
        arm: armSide,
        reverseExtraFactor: extra.reversed ? extraFactor : 0,
    });
    const baseBounds = getBoundsForLocalShapes(baseSlices, innerGroup.x, innerGroup.y, innerGroup.rotation);
    const stretchedBounds = getBoundsForLocalShapes(stretchedSlices, innerGroup.x, innerGroup.y, innerGroup.rotation);

    if (!baseBounds || !stretchedBounds) {
        return { x: 0, y: 0 };
    }

    const xDelta = getNearestEdgeDelta(
        guidePosition.x,
        baseBounds.minX,
        baseBounds.maxX,
        stretchedBounds.minX,
        stretchedBounds.maxX,
    );
    const yDelta = getNearestEdgeDelta(
        guidePosition.y,
        baseBounds.minY,
        baseBounds.maxY,
        stretchedBounds.minY,
        stretchedBounds.maxY,
    );

    return Math.abs(xDelta) > Math.abs(yDelta) ? { x: xDelta, y: 0 } : { x: 0, y: yDelta };
}

const leftInnerSlices = computed(() => {
    if (!props.selectedShape || !activeTemplate.value?.images.inner) return null;
    const innerImage = preloadedImages.value[props.selectedShape].inner;
    if (!innerImage) return null;

    const extra = getInnerExtraStretch({
        armSide: 'left',
        cornerType: props.cornerType,
        longerArm: props.longerArm,
        stretchFactors: endCapStretchFactorsPerShape.value,
    });
    const extraFactor = getTypeOneStretchFactor('left', extra.factor);
    const guideStretchPixels = getPerCornerGuideStretchPixels(
        props.guideTypes[1].rightCorner,
        props.guideTypes[1].leftCorner,
    );
    return buildStretchedArmSlices({
        imageAsset: activeTemplate.value.images.inner,
        stretchReferenceAsset: activeTemplate.value.images.left,
        image: innerImage,
        scale: activeTemplate.value.scale,
        factor: leftProductSizeFactor.value + extraFactor,
        arm: 'left',
        reverseExtraFactor: extra.reversed ? extraFactor : 0,
        cornerExtraPixels: guideStretchPixels.cornerSide,
        farSideExtraPixels: guideStretchPixels.farSide,
    });
});

const rightInnerSlices = computed(() => {
    if (!props.selectedShape || !activeTemplate.value?.images.inner) return null;
    const innerImage = preloadedImages.value[props.selectedShape].inner;
    if (!innerImage) return null;

    const extra = getInnerExtraStretch({
        armSide: 'right',
        cornerType: props.cornerType,
        longerArm: props.longerArm,
        stretchFactors: endCapStretchFactorsPerShape.value,
    });
    const extraFactor = getTypeOneStretchFactor('right', extra.factor);
    const guideStretchPixels = getPerCornerGuideStretchPixels(
        props.guideTypes[2].rightCorner,
        props.guideTypes[2].leftCorner,
    );
    return buildStretchedArmSlices({
        imageAsset: activeTemplate.value.images.inner,
        stretchReferenceAsset: activeTemplate.value.images.right,
        image: innerImage,
        scale: activeTemplate.value.scale,
        factor: rightProductSizeFactor.value + extraFactor,
        arm: 'right',
        reverseExtraFactor: extra.reversed ? extraFactor : 0,
        cornerExtraPixels: guideStretchPixels.cornerSide,
        farSideExtraPixels: guideStretchPixels.farSide,
    });
});

// Local nudge + rotation for each inner overlay, applied via a nested group so
// the whole inner image (all its slices) transforms as one. x / y are arm-local
// offsets and rotate spins it around the group origin; the values live in
// leftInner / rightInner in JointsConfiguration.ts.
const leftInnerGroupConfig = computed(() => {
    const inner = activeTemplate.value?.leftInner;
    const shifted = applyArmStretchShift(
        inner?.x ?? 0,
        inner?.y ?? 0,
        ARM_STRETCH_DIRECTION.left,
        getArmProductSizeFactor('left'),
        activeTemplate.value,
    );
    return { x: shifted.x, y: shifted.y, rotation: inner?.rotate ?? 0 };
});

const rightInnerGroupConfig = computed(() => {
    const inner = activeTemplate.value?.rightInner;
    const shifted = applyArmStretchShift(
        inner?.x ?? 0,
        inner?.y ?? 0,
        ARM_STRETCH_DIRECTION.right,
        getArmProductSizeFactor('right'),
        activeTemplate.value,
    );
    return { x: shifted.x, y: shifted.y, rotation: inner?.rotate ?? 0 };
});

function buildArmDragHitAreaConfig(slices: LocalShape[] | null, arm: 'left' | 'right') {
    if (
        !activeTemplate.value ||
        activeTemplate.value.rotatingHalf !== arm ||
        !showAngleIndicator.value ||
        !slices?.length
    ) {
        return null;
    }

    const hitAreaPadding = 12;
    const minX = Math.min(...slices.map((slice) => slice.x)) - hitAreaPadding;
    const minY = Math.min(...slices.map((slice) => slice.y)) - hitAreaPadding;
    const maxX = Math.max(...slices.map((slice) => slice.x + slice.width)) + hitAreaPadding;
    const maxY = Math.max(...slices.map((slice) => slice.y + slice.height)) + hitAreaPadding;

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        fill: 'rgba(255, 255, 255, 0.01)',
        strokeEnabled: false,
        listening: true,
    };
}

const leftArmDragHitAreaConfig = computed(() => buildArmDragHitAreaConfig(leftImageSlices.value, 'left'));

const rightArmDragHitAreaConfig = computed(() => buildArmDragHitAreaConfig(rightImageSlices.value, 'right'));

function buildAssemblyPositionLabelConfig(slices: LocalShape[] | null, label: string, visible: boolean) {
    if (!visible || props.currentStep < 2 || !slices?.length) {
        return null;
    }

    const minX = Math.min(...slices.map((slice) => slice.x));
    const minY = Math.min(...slices.map((slice) => slice.y));
    const maxX = Math.max(...slices.map((slice) => slice.x + slice.width));
    const maxY = Math.max(...slices.map((slice) => slice.y + slice.height));
    const width = maxX - minX;
    const height = maxY - minY;
    const vertical = height > width;

    return {
        x: vertical ? minX + width / 2 - 4 : minX + width / 2 - 4,
        y: vertical ? minY + height / 2 - 7 : minY + height / 2 - 7,
        text: label,
        fontSize: 12,
        fontStyle: 'bold',
        fill: '#424242',
        listening: false,
    };
}

const leftAssemblyPositionLabelConfig = computed(() => {
    // console.log('leftImageSlices.value', leftImageSlices.value, props);
    return buildAssemblyPositionLabelConfig(leftImageSlices.value, '1', props.assemblyPositionVisibility.left);
});

const rightAssemblyPositionLabelConfig = computed(() =>
    buildAssemblyPositionLabelConfig(rightImageSlices.value, '2', props.assemblyPositionVisibility.right),
);

// ============================================================================
// STATIC DOTTED LINES (per arm, per shape)
// ---------------------------------------------------------------------------
// Drawn INSIDE each arm's group — coords are LOCAL to the arm's anchor, so
// they rotate with the arm. Not angle-dependent.
//   - left arm anchor  = image's bottom-right corner (arm extends into negative x/y)
//   - right arm anchor = image's top-left corner     (arm extends into positive x/y)
//
// Each line is defined by a start point and an end point:
//   startX / startY  — where the line begins
//   endX   / endY    — where the line ends
// Length = distance between those two points. For a vertical line on the left
// arm change endY; for a horizontal line on the right arm change endX.
// ============================================================================
const staticArmLinesPerShape: Record<JointShape, { left: StaticArmLine[]; right: StaticArmLine[] }> = {
    external: {
        // Left arm is HORIZONTAL — anchor is image bottom-right, arm extends left (negative X).
        // Lines run horizontally along the top and bottom edges of the arm.
        // Shift Y to move up/down; change endX (more negative = longer line).
        left: [
            // Bottom edge of the horizontal arm (closest to corner, Y near 0)
            { startX: -78, startY: -35, endX: -175, endY: -35 },
            // Top edge of the horizontal arm
            { startX: -60, startY: -78, endX: -195, endY: -78 },
        ],
        // Right arm is VERTICAL — anchor is image top-left, arm extends down (positive Y).
        // Lines run vertically along the left and right edges of the arm.
        // Shift X to move left/right; change endY (larger = longer line).
        right: [
            // Left edge of the vertical arm (closest to corner, X near 0)
            { startX: 32, startY: 33, endX: 32, endY: 137 },
            // Right edge of the vertical arm
            { startX: 74, startY: 17, endX: 74, endY: 153 },
        ],
    },
    internal: {
        left: [
            { startX: 0, startY: 0, endX: 0, endY: -135 },
            { startX: -42, startY: -20, endX: -42, endY: -117 },
        ],
        right: [
            { startX: 43, startY: 0, endX: 180, endY: 0 },
            { startX: 60, startY: 43, endX: 160, endY: 43 },
        ],
    },
};

const STATIC_LINE_STYLE = {
    stroke: '#9e9e9e',
    strokeWidth: 1.5,
    lineCap: 'round',
    lineJoin: 'round',
    dash: [3, 3],
} as const;

const INNER_ARC_LINE_INWARD_PIXELS = 8;
const INNER_ARC_LINE_OFFSET = computed<Record<ArmSide, { x: number; y: number }>>(() => ({
    left: {
        x: isSelectedNonRegularGuide(props.guideTypes[1].rightCorner) ? -INNER_ARC_LINE_INWARD_PIXELS : -1,
        y: 5,
    },
    right: {
        x: isSelectedNonRegularGuide(props.guideTypes[2].leftCorner) ? -INNER_ARC_LINE_INWARD_PIXELS : -1,
        y: 26,
    },
}));

function buildInnerArcLineConfig(slices: LocalShape[] | null, armSide: ArmSide) {
    if (!slices?.length) return null;
    const offset = INNER_ARC_LINE_OFFSET.value[armSide];
    const x = Math.max(...slices.map((slice) => slice.x + slice.width)) + offset.x;
    return { ...STATIC_LINE_STYLE, points: [x, offset.y, x, offset.y - ARC_RADIUS] };
}

const leftInnerArcLineConfig = computed(() => buildInnerArcLineConfig(leftInnerSlices.value, 'left'));
const rightInnerArcLineConfig = computed(() => buildInnerArcLineConfig(rightInnerSlices.value, 'right'));

const leftArmStaticLineConfigs = computed(() => {
    if (!props.selectedShape) return [];
    return staticArmLinesPerShape[props.selectedShape].left.map((line) => {
        const start = applyArmStretchShift(
            line.startX,
            line.startY,
            ARM_STRETCH_DIRECTION.left,
            getArmProductSizeFactor('left'),
            activeTemplate.value,
        );
        const end = applyArmStretchShift(
            line.endX,
            line.endY,
            ARM_STRETCH_DIRECTION.left,
            getArmProductSizeFactor('left'),
            activeTemplate.value,
        );
        return {
            ...STATIC_LINE_STYLE,
            points: [start.x, start.y, end.x, end.y],
        };
    });
});

const rightArmStaticLineConfigs = computed(() => {
    if (!props.selectedShape) return [];
    return staticArmLinesPerShape[props.selectedShape].right.map((line) => {
        const start = applyArmStretchShift(
            line.startX,
            line.startY,
            ARM_STRETCH_DIRECTION.right,
            getArmProductSizeFactor('right'),
            activeTemplate.value,
        );
        const end = applyArmStretchShift(
            line.endX,
            line.endY,
            ARM_STRETCH_DIRECTION.right,
            getArmProductSizeFactor('right'),
            activeTemplate.value,
        );
        return {
            ...STATIC_LINE_STYLE,
            points: [start.x, start.y, end.x, end.y],
        };
    });
});

const { leftInnerShaderConfig, rightInnerShaderConfig } = useInnerShader({
    shading: computed(() => activeTemplate.value?.innerSurroundShading),
    leftBorderLines: leftArmStaticLineConfigs,
    rightBorderLines: rightArmStaticLineConfigs,
    leftArmSlices: positionedLeftImageSlices,
    rightArmSlices: rightImageSlices,
    leftInnerBorder: leftInnerArcLineConfig,
    rightInnerBorder: rightInnerArcLineConfig,
    leftInnerGroup: leftInnerGroupConfig,
    rightInnerGroup: rightInnerGroupConfig,
});

// ============================================================================
// SLAT CONNECTOR
// ---------------------------------------------------------------------------
// A decorative image placed at the active corner (where the ARK / arc sits)
// only when the guide type is 'no-guide'. Position, rotation and scale are
// driven by the per-shape `slatConnector*` values in
// `activePivotGlobalConfiguration` so each shape can be tweaked independently.
// ============================================================================
const slatConnectorImageElement = computed(() => {
    const slatConnectorUrl = activeTemplate.value?.slatConnectorUrl;
    return slatConnectorUrl ? createImage(slatConnectorUrl) : null;
});

const areInnerGuidesSetToNoGuides = computed(() => {
    return props.guideTypes[1].rightCorner === 'no-guide' && props.guideTypes[2].leftCorner === 'no-guide';
});

const slatConnectorImageConfig = computed(() => {
    if (!areInnerGuidesSetToNoGuides.value || !props.selectedShape || !activeTemplate.value) {
        return null;
    }

    const pivotConfig = activePivotGlobalConfiguration.value;
    if (!pivotConfig) {
        return null;
    }

    const {
        arcAnchorX,
        arcAnchorY,
        slatConnectorX,
        slatConnectorY,
        slatConnectorWidthMm,
        slatConnectorHeightMm,
        slatConnectorRotation,
        slatConnectorScale,
    } = pivotConfig;
    const { scale } = activeTemplate.value;
    if (!slatConnectorImageElement.value) {
        return null;
    }
    return {
        image: slatConnectorImageElement.value,
        x: arcAnchorX + slatConnectorX,
        y: arcAnchorY + slatConnectorY,
        width: slatConnectorWidthMm * scale * slatConnectorScale,
        height: slatConnectorHeightMm * scale * slatConnectorScale,
        rotation: slatConnectorRotation,
    };
});

const { interiorAreaConfig, interiorAreaTextConfig } = useInteriorArea({
    selectedShape: computed(() => props.selectedShape),
    angle: computed(() => props.angle),
    activeTemplate,
    stageConfig,
    contentGroupConfig,
    activePivotGlobalConfiguration,
    activeArcVisualConfig,
    extensionTriangleRadialOffsetPerShape,
    arcRadius: computed(() => {
        const { startRadius, endRadius } = getArcEdgeRadii(props.angle);
        return (startRadius + endRadius) / 2;
    }),
    getInteriorText: () => proxy?.$t('Interior') as string,
});

return { __sfc: true,props, proxy, emit, showLeftArm, showRightArm, createImage, preloadedImages, activeTemplate, activePivotGlobalConfiguration, getProductIdByArmSide, leftProductSizeFactor, rightProductSizeFactor, MIN_STAGE_PADDING, STAGE_PADDING_RATIO, snapCanvasPixel, getWorldBoundsForLocalShape, getSweptWorldBoundsForLocalShape, getAngleLabelBounds, getStableAngleLabelBounds, mergeBounds, stableContentBounds, stagePadding, stageConfig, globalOffsetsPerShape, contentGroupConfig, activeRotationDeg, arcVisualConfigPerShape, leftArmGroupConfig, leftImageGroupConfig, rightArmGroupConfig, buildGuideKonvaConfig, guideSlotFallbackArmSide, getArmProductSizeFactor, preloadedGuideImages, getGuideImage, getSelectedGuideAsset, buildSelectedGuideConfig, leftTopGuideConfig, leftBottomGuideConfig, rightTopGuideConfig, rightBottomGuideConfig, ARC_RADIUS, ARC_SAMPLES, EXTENSION_RADIUS, activeArcVisualConfig, transformArmLocalPointToWorld, getRayToLineDistance, getArcEdgeRadii, isAngleDragStep, activeDraggableArm, angleDragState, ANGLE_DRAG_PIXELS_PER_DEGREE, dragDirectionPerShape, clampAngle, getClientPointFromEvent, updateAngleFromPointerEvent, addAngleDragListeners, removeAngleDragListeners, setStageCursor, setArmCursor, resetArmCursor, startAngleDrag, stopAngleDrag, arcConfig, extensionTriangleRadialOffsetPerShape, edgeLineRadialOffsetPerShape, secondEdgeLineXOffsetPerShape, extensionLineConfigs, edgeLineConfigs, pivotDotConfig, angleLabelConfig, showAngleIndicator, leftImageSlices, positionedLeftImageSlices, rightImageSlices, getTypeOneStretchFactor, endCapStretchFactorsPerShape, getBoundsForLocalShapes, getInnerGuideStretchShift, leftInnerSlices, rightInnerSlices, leftInnerGroupConfig, rightInnerGroupConfig, buildArmDragHitAreaConfig, leftArmDragHitAreaConfig, rightArmDragHitAreaConfig, buildAssemblyPositionLabelConfig, leftAssemblyPositionLabelConfig, rightAssemblyPositionLabelConfig, staticArmLinesPerShape, STATIC_LINE_STYLE, INNER_ARC_LINE_INWARD_PIXELS, INNER_ARC_LINE_OFFSET, buildInnerArcLineConfig, leftInnerArcLineConfig, rightInnerArcLineConfig, leftArmStaticLineConfigs, rightArmStaticLineConfigs, leftInnerShaderConfig, rightInnerShaderConfig, slatConnectorImageElement, areInnerGuidesSetToNoGuides, slatConnectorImageConfig, interiorAreaConfig, interiorAreaTextConfig }
}

})